import { Component } from '@theme/component';

/**
 * A custom element that renders a video background.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  /** @type {IntersectionObserver | null} */
  #io = null;

  connectedCallback() {
    super.connectedCallback();

    const { videoSources, videoElement } = this.refs;

    const start = () => {
      for (const source of videoSources) {
        const { videoSource } = source.dataset;
        if (videoSource) source.setAttribute('src', videoSource);
      }
      videoElement.load();
      videoElement.play?.().catch(() => {});
    };

    if (!('IntersectionObserver' in window)) {
      start();
      return;
    }

    this.#io = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        this.#io?.disconnect();
        this.#io = null;
        start();
      },
      { rootMargin: '280px 0px', threshold: 0.01 }
    );
    this.#io.observe(this);
  }

  disconnectedCallback() {
    this.#io?.disconnect();
    this.#io = null;
    super.disconnectedCallback();
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
